import React, { useState, useCallback, useEffect } from 'react'
import { useWallet } from '@solana/wallet-adapter-react'
import { WalletName } from '@solana/wallet-adapter-base'
import { X, Wallet, ExternalLink, Shield, QrCode, CreditCard, HelpCircle, CheckCircle, Info } from 'lucide-react'
import { WalletRecommendations } from './WalletRecommendations'
import { log } from '@/utils/logger'

interface CustomWalletModalProps {
  visible: boolean
  onClose: () => void
}

export const CustomWalletModal: React.FC<CustomWalletModalProps> = ({ visible, onClose }) => {
  const { wallets, select, connect, connecting, connected } = useWallet()
  const [isConnecting, setIsConnecting] = useState(false)
  const [showRecommendations, setShowRecommendations] = useState(false)
  const [connectionStatus, setConnectionStatus] = useState<string | null>(null)

  // Auto-close modal when wallet connects successfully
  useEffect(() => {
    if (connected) {
      setIsConnecting(false)
      setConnectionStatus('Connected successfully!')
      setTimeout(() => {
        onClose()
        setConnectionStatus(null)
      }, 1000)
    } else if (!connecting && isConnecting) {
      // Reset connecting state if wallet connection fails or is cancelled
      setIsConnecting(false)
      setConnectionStatus(null)
    }
  }, [connected, connecting, isConnecting, onClose])

  // Add global unhandled promise rejection handler for wallet errors
  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason
      if (error?.code === 4001 || error?.message?.includes('User rejected')) {
        // Prevent the uncaught promise rejection for user cancellations
        event.preventDefault()
        log.wallet('Prevented uncaught promise rejection for user wallet rejection')
      }
    }

    window.addEventListener('unhandledrejection', handleUnhandledRejection)

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
    }
  }, [])

  // Helper function to detect mobile devices
  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  }

  // Helper function to get wallet app store URLs
  const getWalletStoreUrls = (walletName: WalletName): { ios: string, android: string } | null => {
    switch (walletName) {
      case 'Phantom':
        return {
          ios: 'https://apps.apple.com/app/phantom-solana-wallet/id1598432977',
          android: 'https://play.google.com/store/apps/details?id=app.phantom'
        }
      case 'Solflare':
        return {
          ios: 'https://apps.apple.com/app/solflare/id1580902717',
          android: 'https://play.google.com/store/apps/details?id=com.solflare.mobile'
        }
      case 'Backpack':
        return {
          ios: 'https://apps.apple.com/app/backpack-wallet/id1625830239',
          android: 'https://play.google.com/store/apps/details?id=app.backpack.mobile'
        }
      default:
        return null
    }
  }

  // Helper function to create mobile wallet connection URLs that maintain browser context
  const getMobileWalletConnectionUrl = (walletName: WalletName): string | null => {
    // For mobile web apps, we want to trigger wallet connection without leaving the browser
    // This uses wallet-specific URL schemes that keep the browser in context
    const currentUrl = encodeURIComponent(window.location.href)
    const referrer = encodeURIComponent(window.location.hostname)

    switch (walletName) {
      case 'Phantom':
        // Use Phantom's universal link format that returns to browser
        return `https://phantom.app/ul/browse/${currentUrl}?ref=${referrer}`
      case 'Solflare':
        // Use Solflare's universal link format
        return `https://solflare.com/ul/v1/browse?url=${currentUrl}&ref=${referrer}`
      case 'Backpack':
        // Use Backpack's universal link format
        return `https://backpack.app/ul/browse?url=${currentUrl}&ref=${referrer}`
      default:
        return null
    }
  }

  const handleWalletSelect = useCallback(async (walletName: WalletName) => {
    log.wallet('Attempting to select wallet:', walletName)

    try {
      setIsConnecting(true)

      // Find the specific wallet adapter
      const selectedWallet = wallets.find(wallet => wallet.adapter.name === walletName)
      if (!selectedWallet) {
        log.error('Wallet not found:', walletName, 'Available wallets:', wallets.map(w => w.adapter.name))
        setIsConnecting(false)
        return
      }

      log.wallet('Found wallet adapter:', selectedWallet.adapter.name, 'Ready state:', selectedWallet.readyState)

      // Check if wallet is ready to connect
      const onMobile = isMobile()
      const walletNotInstalled = selectedWallet.readyState !== 'Installed'

      // On mobile without extension installed, use Mobile Wallet Adapter
      if (onMobile && walletNotInstalled) {
        log.wallet('Mobile device without extension - using Mobile Wallet Adapter protocol')

        // Find the Mobile Wallet Adapter
        const mobileAdapter = wallets.find(w => w.adapter.name === 'Mobile Wallet Adapter')
        if (mobileAdapter) {
          log.wallet('Found Mobile Wallet Adapter, selecting it for mobile deep link')
          setConnectionStatus(`Opening ${walletName} app...`)

          // Select the Mobile Wallet Adapter - it will handle the deep link
          select(mobileAdapter.adapter.name)

          // Wait a moment then trigger connect
          await new Promise(resolve => setTimeout(resolve, 500))

          try {
            await connect()
            // Connection successful - close modal
            return
          } catch (mwaError) {
            log.wallet('Mobile Wallet Adapter error:', mwaError)
            setConnectionStatus('Failed to connect. Please ensure the wallet app is installed.')
            setIsConnecting(false)
            setTimeout(() => setConnectionStatus(null), 3000)
            return
          }
        } else {
          log.error('Mobile Wallet Adapter not found - cannot connect on mobile')
          setConnectionStatus('Mobile wallet connection not available')
          setIsConnecting(false)
          setTimeout(() => setConnectionStatus(null), 3000)
          return
        }
      }

      // Desktop without extension - prompt to install
      if (!onMobile && walletNotInstalled) {
        log.wallet('Wallet not installed on desktop, opening extension page')
        if (selectedWallet.adapter.url) {
          window.open(selectedWallet.adapter.url, '_blank')
        }
        setIsConnecting(false)
        return
      }

      // Desktop with extension installed
      // Select the wallet first
      log.wallet('Selecting wallet...')
      select(selectedWallet.adapter.name)
      
      // Wait for selection to complete and verify wallet is selected
      await new Promise(resolve => setTimeout(resolve, 500))
      
      // Then explicitly trigger connection to show wallet popup
      log.wallet('Connecting to wallet...')
      try {
        await connect()
      } catch (connectError) {
        // Handle user rejection gracefully
        if (connectError?.code === 4001 || connectError?.message?.includes('User rejected')) {
          log.wallet('User rejected wallet connection')
          setIsConnecting(false)
          setConnectionStatus(null)
          return
        }

        // If connection fails due to wallet not being selected, try with more retries
        if (connectError?.name === 'WalletNotSelectedError') {
          log.wallet('Wallet not selected, retrying with longer wait...')
          await new Promise(resolve => setTimeout(resolve, 800))

          try {
            await connect()
          } catch (secondError) {
            // Handle user rejection on retry
            if (secondError?.code === 4001 || secondError?.message?.includes('User rejected')) {
              log.wallet('User rejected wallet connection on retry')
              setIsConnecting(false)
              setConnectionStatus(null)
              return
            }

            if (secondError?.name === 'WalletNotSelectedError') {
              log.info('Second retry...')
              await new Promise(resolve => setTimeout(resolve, 1000))
              try {
                await connect()
              } catch (thirdError) {
                // Handle user rejection on final retry
                if (thirdError?.code === 4001 || thirdError?.message?.includes('User rejected')) {
                  log.wallet('User rejected wallet connection on final retry')
                  setIsConnecting(false)
                  setConnectionStatus(null)
                  return
                }
                throw thirdError
              }
            } else {
              throw secondError
            }
          }
        } else {
          throw connectError
        }
      }
      
      // If we reach here, connection was successful
      log.wallet('Wallet connection successful')
      
    } catch (error) {
      // Handle user rejection gracefully at the top level
      if (error?.code === 4001 || error?.message?.includes('User rejected')) {
        log.wallet('User rejected wallet connection (top level)')
        setIsConnecting(false)
        setConnectionStatus(null)
        return
      }

      // Don't log WalletNotSelectedError as it's usually recoverable
      if (error?.name !== 'WalletNotSelectedError') {
        log.error('Failed to select wallet:', error)
      }
      setIsConnecting(false)

      // Only show alert for actual connection failures
      // Don't show alerts for user cancellations, recoverable errors, or WalletNotSelectedError
      if (error?.message &&
          !error.message.includes('User rejected') &&
          !error.message.includes('cancelled') &&
          error?.code !== 4001 &&
          error?.name !== 'WalletNotSelectedError') {
        alert(`Failed to connect to ${walletName}: ${error.message}`)
      }
    }
  }, [wallets, select, connect])

  if (!visible) return null

  // Filter and deduplicate wallets to prevent key conflicts, excluding MetaMask
  const uniqueWallets = wallets.filter((wallet, index, self) => {
    // Exclude MetaMask to prevent conflicts with Solana wallets
    if (wallet.adapter.name === 'MetaMask') {
      return false
    }
    return self.findIndex(w => w.adapter.name === wallet.adapter.name) === index
  })

  // Show supported wallets - only Backpack is officially supported for X1
  // Note: Mobile Wallet Adapter is hidden from UI - it's used internally for mobile connections
  const supportedWallets = uniqueWallets.filter(wallet =>
    [
      // Native X1 Wallet + Backpack are officially supported for X1
      'X1 Wallet',
      'Backpack'
    ].includes(wallet.adapter.name)
  )

  // Sort wallets with priority order: X1 Wallet first (native), then Backpack
  const sortedWallets = supportedWallets.sort((a, b) => {
    const priority = ['X1 Wallet', 'Backpack', 'Phantom', 'Solflare', 'Mobile Wallet Adapter']
    const aIndex = priority.indexOf(a.adapter.name)
    const bIndex = priority.indexOf(b.adapter.name)

    // If both are in priority list, sort by priority
    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex
    // If only a is in priority, it comes first
    if (aIndex !== -1) return -1
    // If only b is in priority, it comes first
    if (bIndex !== -1) return 1
    // Otherwise maintain original order
    return 0
  })

  log.wallet('Available wallets:', uniqueWallets.map(w => w.adapter.name))
  log.wallet('Supported wallets:', sortedWallets.map(w => w.adapter.name))

  // All wallets are hot wallets for now
  const hotWallets = sortedWallets
  const hardwareWallets = [] // Will be populated when we add official hardware wallet adapters

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-gray-900 rounded-xl border border-gray-700 p-4 w-full max-w-md mx-4 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <Wallet className="h-5 w-5 text-blue-400" />
            <h2 className="text-xl font-semibold text-white">Connect Wallet</h2>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowRecommendations(true)}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors flex items-center space-x-1 text-gray-400 hover:text-blue-400"
              title="Get wallet recommendations"
            >
              <HelpCircle className="h-4 w-4" />
              <span className="text-sm">Help me choose</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Mobile Info Banner */}
        {isMobile() && (
          <div className="mb-4 p-3 bg-blue-900/20 border border-blue-700/30 rounded-lg">
            <div className="flex items-center space-x-2 mb-1">
              <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
              <span className="text-sm font-medium text-blue-400">Mobile Device Detected</span>
            </div>
            <p className="text-xs text-gray-300">
              Connect using browser extensions or mobile apps. After connecting in the wallet app, return to this page to continue.
            </p>
          </div>
        )}

        {/* Connection Status */}
        {connectionStatus && (
          <div className="mb-4 p-3 bg-yellow-900/20 border border-yellow-700/30 rounded-lg">
            <div className="flex items-center space-x-2">
              {connecting || isConnecting ? (
                <div className="w-4 h-4 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin flex-shrink-0"></div>
              ) : connected ? (
                <CheckCircle className="h-4 w-4 text-green-400 flex-shrink-0" />
              ) : (
                <Info className="h-4 w-4 text-yellow-400 flex-shrink-0" />
              )}
              <span className="text-sm text-yellow-200">{connectionStatus}</span>
            </div>
          </div>
        )}

        {/* Hot Wallets Section */}
        {hotWallets.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider flex items-center">
              <Wallet className="h-4 w-4 mr-2" />
              {isMobile() ? 'Mobile Wallets' : 'Browser & Mobile Wallets'}
            </h3>
            <div className="space-y-2">
              {hotWallets.map((wallet) => {
                const isReady = wallet.readyState === 'Installed'
                const isLoading = connecting && isConnecting

                // Check if wallet is available on mobile (including mobile apps)
                const isMobileAvailable = isMobile() && wallet.adapter.name === 'Backpack'
                const isAvailable = isReady || isMobileAvailable

                return (
                  <button
                    key={`wallet-${wallet.adapter.name}-${wallet.readyState}`}
                    onClick={() => {
                      log.wallet('Button clicked for wallet:', wallet.adapter.name, 'Available:', isAvailable)
                      handleWalletSelect(wallet.adapter.name)
                    }}
                    disabled={isLoading}
                    className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all ${
                      isAvailable
                        ? 'bg-gray-800 border-gray-600 hover:bg-gray-700 hover:border-blue-500'
                        : 'bg-gray-800/50 border-gray-700 hover:bg-gray-800'
                    } ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <div className="flex items-center space-x-3">
                      <img
                        src={wallet.adapter.icon}
                        alt={wallet.adapter.name}
                        className="w-7 h-7 rounded-lg"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement
                          target.style.display = 'none'
                        }}
                      />
                      <div className="text-left">
                        <p className="font-medium text-white">{wallet.adapter.name}</p>
                        <p className="text-sm text-gray-400">
                          {isMobile()
                            ? (isReady ? 'Browser extension' : isMobileAvailable ? 'Mobile app available' : 'Install mobile app')
                            : (isReady ? 'Ready to connect' : 'Install browser extension')
                          }
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      {isLoading ? (
                        <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                      ) : !isAvailable ? (
                        <ExternalLink className="h-4 w-4 text-gray-400" />
                      ) : (
                        <div className={`w-3 h-3 rounded-full ${isAvailable ? 'bg-green-400' : 'bg-gray-400'}`} />
                      )}
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {/* Hardware Wallets Section */}
        {hardwareWallets.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider flex items-center">
              <Shield className="h-4 w-4 mr-2" />
              Hardware Wallets (Cold Storage)
            </h3>
            <div className="space-y-2">
              {hardwareWallets.map((wallet) => {
                const getHardwareIcon = (name: string) => {
                  if (name === 'Ledger') return <Shield className="h-6 w-6 text-blue-400" />
                  if (name === 'Keystone') return <QrCode className="h-6 w-6 text-purple-400" />
                  if (name === 'Tangem') return <CreditCard className="h-6 w-6 text-green-400" />
                  if (name === 'ELLIPAL') return <QrCode className="h-6 w-6 text-orange-400" />
                  if (name === 'Trezor') return <Shield className="h-6 w-6 text-emerald-400" />
                  return <Shield className="h-6 w-6 text-gray-400" />
                }
                
                const getDescription = (name: string) => {
                  if (name === 'Ledger') return 'USB/Bluetooth • Secure Element'
                  if (name === 'Keystone') return 'QR Code • Air-gapped'
                  if (name === 'Tangem') return 'NFC Card • No seed phrase'
                  if (name === 'ELLIPAL') return 'QR Code • Touchscreen'
                  if (name === 'Trezor') return 'USB • Open Source'
                  return 'Hardware security'
                }
                
                return (
                  <button
                    key={`hw-wallet-${wallet.adapter.name}`}
                    onClick={() => handleWalletSelect(wallet.adapter.name)}
                    className="w-full flex items-center justify-between p-3 rounded-lg border border-amber-500/20 bg-amber-500/10 hover:bg-amber-500/20 transition-all cursor-pointer"
                  >
                    <div className="flex items-center space-x-3">
                      {getHardwareIcon(wallet.adapter.name)}
                      <div className="text-left">
                        <p className="font-medium text-white">{wallet.adapter.name}</p>
                        <p className="text-sm text-amber-200">{getDescription(wallet.adapter.name)}</p>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <p className="text-xs text-amber-300 font-medium">ULTIMATE SECURITY</p>
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {/* No wallets available */}
        {supportedWallets.length === 0 && (
          <div className="text-center py-6">
            <Wallet className="h-10 w-10 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400 mb-4">No supported wallets found</p>
            <p className="text-sm text-gray-500">
              Please install a compatible wallet to continue
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-gray-700">
          <p className="text-xs text-gray-500 text-center">
            By connecting a wallet, you agree to the Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
      
      {/* Hardware wallets will use official Solana wallet adapters */}
      
      {/* Wallet Recommendations Modal */}
      <WalletRecommendations
        visible={showRecommendations}
        onClose={() => setShowRecommendations(false)}
        onWalletSelect={(walletName) => {
          setShowRecommendations(false)
          handleWalletSelect(walletName as WalletName)
        }}
      />
    </div>
  )
}

export default CustomWalletModal